/**
 * The two reference panels of the farm auction: who holds what now, and every past sale.
 *
 * Tonight's wins are marked NEW and counted, so a GM at his cap can see why. Past sales carry
 * what became of the player, because the point of the filter is "what has this GM paid and what
 * did it return".
 *
 * TWO EXPORTS, NOT ONE PAIR (Tim, 9 September: "move 'farm rosters' to rail under prospect lists;
 * extend width of past auctions panel"). They were a `1fr 1fr` grid under the board, which made
 * them each other's width — and they are not the same kind of thing. Rosters is a standing
 * reference read down a column, which is what a rail is; Past auctions is a filtered table whose
 * rows were being folded in half to make room for it. Split apart, each one gets the shape it
 * wanted: the rail's width for the list, the main column's whole width for the table.
 */

import { useMemo, useState } from 'react';
import { faab } from '../domain/faab.js';
import { managerShort } from '../domain/identity.js';
import { LEAGUE_2026 } from '../domain/config.js';
import { farmRosters, outcomeOf, salesByYear, salesTonight, spendOf } from '../domain/farmHistory.js';
import type { FarmNomination } from '../store/types.js';

const CAP = LEAGUE_2026.maxFarmPlayers;

export function FarmRosters(
  { order, tonight }: { order: readonly string[]; tonight: readonly FarmNomination[] },
) {
  /** Won tonight, per GM — not in the log yet, and the reason a cap can bite mid-auction. */
  const wonTonight = useMemo(() => {
    const m = new Map<string, FarmNomination[]>();
    for (const n of tonight) {
      if (!n.winner || !n.player) continue;
      const gm = n.winner.toUpperCase();
      (m.get(gm) ?? m.set(gm, []).get(gm)!).push(n);
    }
    return m;
  }, [tonight]);

  const rosters = useMemo(() => farmRosters(order), [order]);
  const heldCount = rosters.reduce(
    (n, r) => n + r.held.length + (wonTonight.get(r.gm.toUpperCase())?.length ?? 0),
    0,
  );

  return (
      <section className="card railsec farmroster">
        <div className="striptop">
          <h3 className="striphead">Farm rosters now</h3>
          <span className="mut">{heldCount} of {order.length * CAP} slots held</span>
        </div>
        <ul>
          {rosters.map(({ gm, held }) => {
            const fresh = wonTonight.get(gm.toUpperCase()) ?? [];
            const n = held.length + fresh.length;
            return (
              <li key={gm}>
                <span className="frname">{managerShort(gm)}</span>
                <div className="frheld">
                  {held.map((r) => (
                    <div className="frrow" key={`${r.player}${r.farmYear}`}>
                      <span className="pn">{r.player}</span>
                      {r.pos && <span className={`poschip ident pos-${r.pos}`}>{r.pos}</span>}
                      <span className="mut sch">{r.school ?? r.college ?? ''}</span>
                      <span className="mut cost">
                        {r.farmYear} · {r.bid != null ? faab(r.bid) : '—'}
                      </span>
                    </div>
                  ))}
                  {fresh.map((f) => (
                    <div className="frrow" key={`t${f.round}.${f.slot}`}>
                      <span className="pn">{f.player}</span>
                      {f.position && <span className={`poschip ident pos-${f.position}`}>{f.position}</span>}
                      <span className="mut sch">{f.school ?? ''}</span>
                      <span className="mut cost">
                        2026 · {f.bid != null ? faab(f.bid) : '—'}
                      </span>
                      <span className="newchip">NEW</span>
                    </div>
                  ))}
                  {n === 0 && <div className="frrow mut">no farm players</div>}
                </div>
                <span className={`frcount ${n >= CAP ? 'full' : ''}`}>{n}/{CAP}</span>
              </li>
            );
          })}
        </ul>
        <p className="railfoot">
          Tonight&rsquo;s wins are marked NEW. A GM at {CAP} of {CAP} cannot bid again.
        </p>
      </section>
  );
}

export function FarmPast(
  { order, tonight = [] }:
  { order: readonly string[]; tonight?: readonly FarmNomination[] },
) {
  const [who, setWho] = useState('');
  /* Tonight's sales sit in this table beside every other year the moment they are hammered. */
  const live = useMemo(() => salesTonight(tonight), [tonight]);
  const seasons = useMemo(() => salesByYear(who, live), [who, live]);
  const total = seasons.reduce((n, s) => n + s.rows.length, 0);
  const spend = who ? spendOf(who) : null;

  return (
      <section className="card farmpast">
        <div className="striptop">
          <h3 className="striphead">Past auctions</h3>
          <span className="mut">{total} sale{total === 1 ? '' : 's'}</span>
        </div>
        <div className="farmfilter">
          <label className="yearpick">
            <span className="mut">GM</span>
            <select value={who} onChange={(e) => setWho(e.target.value)}>
              <option value="">All managers</option>
              {order.map((g) => <option key={g} value={g}>{managerShort(g)}</option>)}
            </select>
          </label>
          {spend && (
            <span className="mut">
              {faab(spend.paid)} spent · {spend.graduated} graduated
            </span>
          )}
        </div>
        <div className="farmscroll">
          {seasons.map(({ year, rows }) => (
            <div key={year}>
              <div className="fyhead">
                <b>{year}</b>
                <span className="mut">{rows.length} sold</span>
              </div>
              {rows.map((r) => {
                const o = outcomeOf(r);
                return (
                  <div className="fsrow" key={`${year}${r.player}`}>
                    <span className="pn">{r.player}</span>
                    {r.pos && <span className={`poschip ident pos-${r.pos}`}>{r.pos}</span>}
                    <span className="mut sch">{r.school ?? r.college ?? ''}</span>
                    <span className="mut gm">{managerShort(r.gm)}</span>
                    <b className="cost">{r.bid != null ? faab(r.bid) : '—'}</b>
                    <span className={`outcome ${o.tone}`} title={r.statusNote || undefined}>
                      {o.text}
                    </span>
                  </div>
                );
              })}
            </div>
          ))}
          {total === 0 && <p className="note">No sales on record for him.</p>}
        </div>
        {/* 13a puts this line under the list, and it is the only instruction the card needs. */}
        <p className="railfoot">
          Filter by manager to see what one GM has paid and what it returned.
        </p>
      </section>
  );
}
